<script lang="ts">
  import { clickOutside } from '../utils/actions/clickOutside'
  import { escapeKey } from '../utils/actions/escapeKey'
  import { tick } from 'svelte'
  import type { Snippet } from 'svelte'

  let {
    open = $bindable(false),
    trigger,
    content,
    position = 'right',
    width = 'w-40',
    class: className = '',
    ariaLabel,
    restoreFocusOnClose = false,
  }: {
    open?: boolean
    trigger: Snippet
    content: Snippet
    position?: 'left' | 'right'
    width?: string
    class?: string
    ariaLabel?: string
    restoreFocusOnClose?: boolean
  } = $props()

  let triggerRef: HTMLButtonElement = $state()!
  let contentRef: HTMLDivElement = $state()!
  let dropdownPosition = $state({ top: 0, left: 0 })
  let isPositioned = $state(false)
  let wasOpen = false

  function handleTriggerClick(e: MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    open = !open
  }

  function close() {
    open = false
    isPositioned = false
  }

  function calculatePosition() {
    if (!triggerRef || !contentRef || !open) return

    const triggerRect = triggerRef.getBoundingClientRect()
    const contentRect = contentRef.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    // Vertical: prefer below, flip above if insufficient space
    const spaceBelow = viewportHeight - triggerRect.bottom
    const dropdownHeight = contentRect.height || 200

    let top =
      spaceBelow >= dropdownHeight + 8
        ? triggerRect.bottom + 4 // Below
        : triggerRect.top - dropdownHeight - 4 // Above

    // Horizontal: respect position prop, clamp to viewport
    const contentWidth = contentRect.width || 160
    let left =
      position === 'right' ? triggerRect.right - contentWidth : triggerRect.left

    // Ensure stays within viewport
    left = Math.max(8, Math.min(left, viewportWidth - contentWidth - 8))

    dropdownPosition = { top, left }
    isPositioned = true
  }

  $effect(() => {
    if (!open) return

    // Calculate position after dropdown renders
    const timeoutId = setTimeout(calculatePosition, 0)

    // Update position on scroll/resize
    const handleUpdate = () => calculatePosition()

    // Capture scrolls from the mobile content region as well as the document.
    window.addEventListener('scroll', handleUpdate, {
      passive: true,
      capture: true,
    })
    window.addEventListener('resize', handleUpdate, { passive: true })

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('scroll', handleUpdate, true)
      window.removeEventListener('resize', handleUpdate)
    }
  })

  $effect(() => {
    if (open) {
      wasOpen = true
      return
    }

    if (wasOpen) {
      wasOpen = false
      if (restoreFocusOnClose) {
        void tick().then(() => triggerRef?.focus())
      }
    }
  })
</script>

<div
  class="dropdown-container {className}"
  use:clickOutside={close}
  use:escapeKey={close}
>
  <button
    type="button"
    bind:this={triggerRef}
    onclick={handleTriggerClick}
    aria-expanded={open}
    aria-haspopup="menu"
    aria-label={ariaLabel}
  >
    {@render trigger()}
  </button>
  {#if open}
    <div
      bind:this={contentRef}
      class="fixed {width} bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
      class:opacity-0={!isPositioned}
      style="top: {dropdownPosition.top}px; left: {dropdownPosition.left}px;"
    >
      {@render content()}
    </div>
  {/if}
</div>

<style>
  .dropdown-container button {
    all: unset;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
  }

  .dropdown-container button:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
    border-radius: 0.25rem;
  }
</style>
